import { Dialog } from '../components/Dialog';
import type { AudioManager } from '../../audio/AudioManager';

const UI_SCALE_KEY = 'officetycoon_ui_scale';
const SCALE_OPTIONS = [
  { value: 0.85, label: 'Small' },
  { value: 1.0, label: 'Normal' },
  { value: 1.15, label: 'Large' },
  { value: 1.3, label: 'Extra Large' },
];

/** Apply saved UI scale on load. Call once at startup. */
export function initUIScale(): void {
  const saved = localStorage.getItem(UI_SCALE_KEY);
  if (saved) {
    document.documentElement.style.setProperty('--ui-scale', saved);
  } else {
    // Default: larger on mobile (touch devices or narrow screens)
    const isMobile = 'ontouchstart' in window || window.innerWidth < 600;
    const defaultScale = isMobile ? 1.15 : 1.0;
    document.documentElement.style.setProperty('--ui-scale', String(defaultScale));
    localStorage.setItem(UI_SCALE_KEY, String(defaultScale));
  }
}

function setUIScale(scale: number): void {
  document.documentElement.style.setProperty('--ui-scale', String(scale));
  localStorage.setItem(UI_SCALE_KEY, String(scale));
}

function getUIScale(): number {
  return parseFloat(localStorage.getItem(UI_SCALE_KEY) ?? '1');
}

/**
 * Settings dialog — volume controls, UI scale, keyboard shortcuts.
 */
export function openSettingsDialog(
  parent: HTMLElement,
  audio: AudioManager | null,
  onClose: () => void,
): void {
  const dialog = new Dialog({
    title: 'Settings',
    onClose,
  });

  const content = document.createElement('div');
  content.style.cssText = 'display:flex;flex-direction:column;gap:16px;min-width:300px;';

  // ─── Audio ───────────────────────────────────────────────
  const audioSection = document.createElement('div');
  audioSection.style.cssText = 'display:flex;flex-direction:column;gap:8px;';

  const audioTitle = document.createElement('div');
  audioTitle.textContent = 'Audio';
  audioTitle.style.cssText = 'font-size:13px;font-weight:600;border-bottom:1px solid var(--border-color);padding-bottom:4px;';
  audioSection.appendChild(audioTitle);

  if (audio) {
    // Music volume
    audioSection.appendChild(createSlider('Music Volume', 70, (val) => {
      audio.setMusicVolume(val / 100);
    }));

    // SFX volume
    audioSection.appendChild(createSlider('SFX Volume', 100, (val) => {
      audio.setSFXVolume(val / 100);
    }));

    // Mute toggle
    const muteRow = document.createElement('div');
    muteRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
    const muteLabel = document.createElement('span');
    muteLabel.textContent = 'Mute All';
    muteLabel.style.cssText = 'font-size:12px;color:var(--color-text-secondary);';
    const muteBtn = document.createElement('button');
    muteBtn.className = `btn${audio.isMuted ? ' btn--active' : ''}`;
    muteBtn.textContent = audio.isMuted ? 'Unmute' : 'Mute';
    muteBtn.style.cssText = 'font-size:11px;';
    muteBtn.addEventListener('click', () => {
      audio.toggleMute();
      muteBtn.textContent = audio.isMuted ? 'Unmute' : 'Mute';
      muteBtn.className = `btn${audio.isMuted ? ' btn--active' : ''}`;
    });
    muteRow.appendChild(muteLabel);
    muteRow.appendChild(muteBtn);
    audioSection.appendChild(muteRow);
  } else {
    const noAudio = document.createElement('div');
    noAudio.textContent = 'Audio not available.';
    noAudio.style.cssText = 'font-size:11px;color:var(--color-text-muted);';
    audioSection.appendChild(noAudio);
  }

  content.appendChild(audioSection);

  // ─── UI Scale ──────────────────────────────────────────────
  const scaleSection = document.createElement('div');
  scaleSection.style.cssText = 'display:flex;flex-direction:column;gap:8px;';

  const scaleTitle = document.createElement('div');
  scaleTitle.textContent = 'UI Size';
  scaleTitle.style.cssText = 'font-size:13px;font-weight:600;border-bottom:1px solid var(--border-color);padding-bottom:4px;';
  scaleSection.appendChild(scaleTitle);

  const scaleRow = document.createElement('div');
  scaleRow.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;';

  const currentScale = getUIScale();
  for (const opt of SCALE_OPTIONS) {
    const btn = document.createElement('button');
    const isActive = Math.abs(currentScale - opt.value) < 0.05;
    btn.className = `btn${isActive ? ' btn--active' : ''}`;
    btn.textContent = opt.label;
    btn.style.cssText = 'font-size:12px;padding:6px 12px;flex:1;';
    btn.addEventListener('click', () => {
      setUIScale(opt.value);
      // Update active state
      for (const b of scaleRow.querySelectorAll('.btn')) {
        b.className = 'btn';
      }
      btn.className = 'btn btn--active';
    });
    scaleRow.appendChild(btn);
  }
  scaleSection.appendChild(scaleRow);
  content.appendChild(scaleSection);

  // ─── Controls ────────────────────────────────────────────
  const controlsSection = document.createElement('div');
  controlsSection.style.cssText = 'display:flex;flex-direction:column;gap:4px;';

  const controlsTitle = document.createElement('div');
  controlsTitle.textContent = 'Keyboard Shortcuts';
  controlsTitle.style.cssText = 'font-size:13px;font-weight:600;border-bottom:1px solid var(--border-color);padding-bottom:4px;';
  controlsSection.appendChild(controlsTitle);

  const shortcuts = [
    ['Space', 'Pause / Resume'],
    ['1 / 2 / 4', 'Set game speed'],
    ['M', 'Toggle mute'],
    ['Escape', 'Pause menu / Close panel'],
  ];

  for (const [key, desc] of shortcuts) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;justify-content:space-between;font-size:11px;padding:2px 0;';
    row.innerHTML = `<kbd style="background:var(--color-bg-primary);padding:1px 6px;border-radius:3px;border:1px solid var(--border-color);font-family:var(--font-pixel);font-size:10px;">${key}</kbd><span style="color:var(--color-text-secondary);">${desc}</span>`;
    controlsSection.appendChild(row);
  }

  content.appendChild(controlsSection);

  // ─── About ───────────────────────────────────────────────
  const aboutSection = document.createElement('div');
  aboutSection.style.cssText = 'font-size:10px;color:var(--color-text-muted);text-align:center;border-top:1px solid var(--border-color);padding-top:8px;';
  aboutSection.textContent = 'Office Tycoon — Game Studio Simulator';
  content.appendChild(aboutSection);

  dialog.body.appendChild(content);
  dialog.mount(parent);
}

function createSlider(label: string, defaultVal: number, onChange: (val: number) => void): HTMLDivElement {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;flex-direction:column;gap:2px;';

  const labelRow = document.createElement('div');
  labelRow.style.cssText = 'display:flex;justify-content:space-between;font-size:11px;';
  const labelEl = document.createElement('span');
  labelEl.textContent = label;
  labelEl.style.color = 'var(--color-text-secondary)';
  const valEl = document.createElement('span');
  valEl.textContent = `${defaultVal}%`;
  valEl.style.cssText = 'font-family:var(--font-pixel);color:var(--color-text-primary);';
  labelRow.appendChild(labelEl);
  labelRow.appendChild(valEl);
  row.appendChild(labelRow);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '100';
  slider.value = String(defaultVal);
  slider.style.cssText = 'width:100%;accent-color:var(--color-accent);';
  slider.addEventListener('input', () => {
    const val = Number(slider.value);
    valEl.textContent = `${val}%`;
    onChange(val);
  });
  row.appendChild(slider);

  return row;
}
